/**
 * The leaderboard score, recomputed on the client so the weights can be dragged live without a
 * round-trip. It mirrors the server's `PerformanceScore` exactly: components that could not be
 * measured are absent (null) and the weights renormalise over whatever is present, so re-weighting
 * never turns "no data" into a zero.
 *
 * <p>The four component values themselves come straight from the API — only their combination is
 * redone here — so the productivity figure stays measured against the same cohort the server used.
 */

export interface Weights {
  onTime: number;
  estimate: number;
  reliability: number;
  productivity: number;
}

export const DEFAULT_WEIGHTS: Weights = {
  onTime: 40,
  estimate: 25,
  reliability: 20,
  productivity: 15,
};

export interface Components {
  onTime: number | null;
  estimate: number | null;
  reliability: number | null;
  productivity: number | null;
}

/** The weighted average of whichever components are present; zero when none are. */
export function composite(components: Components, weights: Weights): number {
  let weightedSum = 0;
  let weightUsed = 0;
  const parts: [number | null, number][] = [
    [components.onTime, weights.onTime],
    [components.estimate, weights.estimate],
    [components.reliability, weights.reliability],
    [components.productivity, weights.productivity],
  ];
  for (const [value, weight] of parts) {
    if (value != null) {
      weightedSum += value * weight;
      weightUsed += weight;
    }
  }
  return weightUsed === 0 ? 0 : weightedSum / weightUsed;
}

/** One to five stars at the same thresholds the server uses: 85, 70, 55, 40. */
export function rating(score: number): number {
  if (score >= 85) return 5;
  if (score >= 70) return 4;
  if (score >= 55) return 3;
  if (score >= 40) return 2;
  return 1;
}
